import { useState } from 'react';
import { CameraIcon, SettingsIcon, SirenIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBackOfficeHubs, type BackOfficeHub } from '@/services/hub.service';
import BackofficePage, {
    DataCard,
    DataTable,
    TH,
    TR,
    TD,
} from '../../components/BackofficePage';
import HubSchedule from './HubSchedule';
import {
    HubIncidentsDialog,
    HubPicturesDialog,
    HubSettingsDialog,
} from './HubDialogs';
import { roughDuration, utcStamp, useNow } from './fieldTime';

const MUTED = 'text-[#6b7480]';

/** A value, or a muted dash for what the hub hasn't told us (or we haven't
 *  written down). */
function Value({ children }: { children: React.ReactNode }) {
    return children == null || children === false || children === '' ? (
        <span className={MUTED}>—</span>
    ) : (
        <>{children}</>
    );
}

/** A second line under a cell's value, for the field that goes with it. */
function Sub({ children }: { children: React.ReactNode }) {
    return <div className={`text-xs ${MUTED}`}>{children}</div>;
}

const ACTION = 'h-7 w-7 text-[#a8b0bb] hover:text-white hover:bg-[#1a2126]';

/** What the Monitoring card opens too: the settings (4G kit, branches, code
 *  updates), the pictures and the incidents. */
function HubActions({ hub }: { hub: BackOfficeHub }) {
    const [open, setOpen] = useState<
        'settings' | 'pictures' | 'incidents' | null
    >(null);
    const toggle = (which: typeof open) => (next: boolean) =>
        setOpen(next ? which : null);
    return (
        <div className="flex justify-end gap-0.5">
            <Button
                variant="ghost"
                size="icon"
                title="Hub settings — 4G, deployment branches, code updates"
                onClick={() => setOpen('settings')}
                className={ACTION}
            >
                <SettingsIcon className="h-3.5 w-3.5" />
            </Button>
            <Button
                variant="ghost"
                size="icon"
                title="View pictures"
                onClick={() => setOpen('pictures')}
                className={ACTION}
            >
                <CameraIcon className="h-3.5 w-3.5" />
            </Button>
            <Button
                variant="ghost"
                size="icon"
                title="Incidents"
                onClick={() => setOpen('incidents')}
                className={ACTION}
            >
                <SirenIcon className="h-3.5 w-3.5" />
            </Button>
            <HubSettingsDialog
                hub={hub}
                open={open === 'settings'}
                onClose={() => setOpen(null)}
            />
            <HubPicturesDialog
                macAddress={hub.macAddress}
                open={open === 'pictures'}
                onOpenChange={toggle('pictures')}
            />
            <HubIncidentsDialog
                macAddress={hub.macAddress}
                open={open === 'incidents'}
                onOpenChange={toggle('incidents')}
            />
        </div>
    );
}

function matches(hub: BackOfficeHub, q: string) {
    return [
        hub.macAddress,
        hub.version,
        hub.scanorhizePiBranch,
        hub.humeosAnsibleBranch,
        hub.simOperator,
        hub.simIccid,
        hub.modem,
        hub.modemOperator,
        hub.modemApn,
        ...hub.scanners.flatMap((s) => [s.serialNumber, s.projectName]),
    ].some((v) => (v ?? '').toLowerCase().includes(q));
}

export default function BackOfficeHubs() {
    const { data, isLoading, error } = useBackOfficeHubs();
    const [query, setQuery] = useState('');
    const now = useNow(60_000);

    if (isLoading)
        return (
            <BackofficePage title="Hubs" description="Loading…">
                <Skeleton className="w-full h-[400px] bg-[#1e242a]" />
            </BackofficePage>
        );
    if (error)
        return (
            <BackofficePage title="Hubs" description="Error">
                <div className="text-red-400 text-sm">{error.toString()}</div>
            </BackofficePage>
        );

    const q = query.trim().toLowerCase();
    const rows = (data ?? []).filter((h) => !q || matches(h, q));

    return (
        <BackofficePage title="Hubs" wide>
            <div className="mb-4">
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Filter by MAC, branch, SIM, modem, scanner or project…"
                    className="w-full max-w-sm bg-[#0f141a] border border-[#1e242a] rounded-md px-3 py-2 text-sm text-[#e5e7e3] placeholder-[#6b7480] focus:outline-none focus:border-[#2d6a4f]"
                />
            </div>
            <DataCard>
                <DataTable>
                    <thead className="bg-[#0f141a]">
                        <tr>
                            <TH>Hub</TH>
                            <TH>Last seen</TH>
                            <TH>Scanners</TH>
                            <TH>Version</TH>
                            <TH>Branches (pi / ansible)</TH>
                            <TH>Schedule</TH>
                            <TH align="right">Code update</TH>
                            <TH align="right">Memory</TH>
                            <TH>SIM</TH>
                            <TH>Modem</TH>
                            <TH>Network (reported)</TH>
                            <TH align="right"> </TH>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((hub) => (
                            <TR key={hub.macAddress}>
                                <TD mono className="whitespace-nowrap">
                                    {hub.macAddress}
                                </TD>
                                <TD className="whitespace-nowrap">
                                    {hub.lastSeenAt ? (
                                        <>
                                            {roughDuration(
                                                now -
                                                    new Date(
                                                        hub.lastSeenAt,
                                                    ).getTime(),
                                            )}{' '}
                                            ago
                                            <Sub>
                                                {utcStamp(hub.lastSeenAt)}
                                            </Sub>
                                        </>
                                    ) : (
                                        <span className={MUTED}>Never</span>
                                    )}
                                </TD>
                                <TD className="whitespace-nowrap">
                                    {hub.scanners.length === 0 ? (
                                        <span className={MUTED}>None</span>
                                    ) : (
                                        hub.scanners.map((s) => (
                                            <div key={s.serialNumber}>
                                                <span className="font-mono text-xs">
                                                    {s.serialNumber}
                                                </span>
                                                {s.projectId && (
                                                    <span
                                                        className={`ml-2 text-xs ${MUTED}`}
                                                    >
                                                        {s.projectName}
                                                    </span>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </TD>
                                <TD mono>{hub.version}</TD>
                                <TD mono className="whitespace-nowrap">
                                    <Value>{hub.scanorhizePiBranch}</Value>
                                    <Sub>
                                        <Value>{hub.humeosAnsibleBranch}</Value>
                                    </Sub>
                                </TD>
                                <TD className="whitespace-nowrap">
                                    {/* Click to change it on the hub. */}
                                    <HubSchedule bare hub={hub} />
                                    <Sub>
                                        <span className="font-mono">
                                            {hub.acquisitionSchedule}
                                        </span>
                                    </Sub>
                                </TD>
                                <TD align="right" className="whitespace-nowrap">
                                    <Value>
                                        {hub.codeUpdateIntervalDays != null &&
                                            `${hub.codeUpdateIntervalDays} d`}
                                    </Value>
                                </TD>
                                <TD align="right" className="whitespace-nowrap">
                                    <Value>
                                        {hub.availableMemoryGB != null &&
                                            `${hub.availableMemoryGB} GB`}
                                    </Value>
                                </TD>
                                <TD className="whitespace-nowrap">
                                    <Value>{hub.simOperator}</Value>
                                    {hub.simIccid && (
                                        <Sub>
                                            <span className="font-mono">
                                                {hub.simIccid}
                                            </span>
                                        </Sub>
                                    )}
                                </TD>
                                <TD>
                                    <Value>{hub.modem}</Value>
                                </TD>
                                <TD className="whitespace-nowrap">
                                    <Value>{hub.modemOperator}</Value>
                                    {hub.modemApn && (
                                        <Sub>
                                            <span className="font-mono">
                                                {hub.modemApn}
                                            </span>
                                        </Sub>
                                    )}
                                </TD>
                                <TD align="right">
                                    <HubActions hub={hub} />
                                </TD>
                            </TR>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td
                                    colSpan={12}
                                    className={`px-4 py-10 text-center text-sm ${MUTED}`}
                                >
                                    No hub.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </DataTable>
            </DataCard>
        </BackofficePage>
    );
}
